import React from 'react';
import type { FC } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  FormHelperText,
  Grid,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import axios from 'axios';
import { useParams } from 'react-router';

interface GencodeRegisterFormProps {
  className?: string;
}

const useStyles = makeStyles(() => ({
  root: {},
  editor: {
    '& .ql-editor': {
      height: 400
    }
  },
  title: {
    textAlign: 'center'
  },
  image: {
    width: 180,
  }
}));

const ManualLinksForm: FC<GencodeRegisterFormProps> = ({ className, ...rest }) => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Formik
      initialValues={{
        code: '',
        submit: null
      }}
      validationSchema={Yup.object().shape({
        code: Yup.string().max(255).required('Download code is required'),
      })}
      onSubmit={async (values, {
        setErrors,
        setStatus,
        setSubmitting,
      }) => {
        try {
          const code = values.code.trim();
          await axios.post('https://conduct.be/api/sendManualLinks', { code });
          setStatus({ success: true });
          setSubmitting(false);
          enqueueSnackbar('Manual links generated - Email sent', {
            variant: 'success'
          });
        } catch (err) {
          console.error(err);
          setStatus({ success: false });
          setErrors({ submit: 'Manual links failed: please check your download code is correct' });
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values
      }) => (
        <form
          onSubmit={handleSubmit}
          className={clsx(classes.root, className)}
          {...rest}
        >
          <Container maxWidth="md">
            <Box>
              <div className={classes.title}>
                <img
                  src={`https://convect.cc/vendor_logos/${vendorId || 'demo'}.png`}
                  alt={vendorId}
                  className={classes.image}/>
                <Typography
                  variant="h4"
                  color="textPrimary"
                >
                  {vendorId && `${vendorId} - `}manual links generation
                </Typography>
              </div>
            </Box>
            <Box mb={4}/>
            <Card>
              <CardHeader title="Please enter your details below to receive your downloadable links."/>
              <Divider />
              <CardContent>
                <Grid
                  container
                  spacing={3}
                >
                  <Grid item xs={12}>
                    <TextField
                      error={Boolean(touched.code && errors.code)}
                      fullWidth
                      helperText={touched.code && errors.code}
                      label="Code"
                      name="code"
                      type="code"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.code}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          {errors.submit && (
            <Box mt={2}>
              <FormHelperText error>
                {errors.submit}
              </FormHelperText>
            </Box>
          )}
          <Box mt={2}>
            <Button
              fullWidth
              color="secondary"
              variant="contained"
              type="submit"
              disabled={isSubmitting}
            >
              Generate Links
            </Button>
          </Box>
          </Container>
        </form>
      )}
    </Formik>
  );
};

ManualLinksForm.propTypes = {
  className: PropTypes.string
};

export default ManualLinksForm;
